import { useState, useEffect, useCallback } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Info,
  X,
  RotateCcw,
  ExternalLink,
  List,
  RefreshCw,
  Copy,
  Check,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SafeErrorCode } from './safeErrorTypes';

// ── Types ──────────────────────────────────────────────────────────────────

export interface ResearchAssistantErrorBannerProps {
  code: SafeErrorCode;
  message: string;
  correlationId: string;
  retriable?: boolean;
  onRetry?: () => void;
  onDismiss?: () => void;
  /** For THREAD_FORBIDDEN / THREAD_NOT_FOUND — opens the thread list sidebar. */
  onOpenThreadList?: () => void;
  /**
   * For ANALYSIS_OUTDATED — invalidates the schema-status cache so the
   * OutdatedAnalysisDialog re-shows. NOT an immediate pipeline kickoff.
   * Per D.2-R3: button label is "Resolve outdated analysis."
   */
  onReRunAnalysis?: () => void;
  /** Seconds remaining before rate-limit lifts. Renders a live countdown chip. */
  retryAfterSec?: number;
}

type Severity = 'error' | 'warning' | 'info';
type PrimaryActionKey =
  | 'retry'
  | 'contactSupport'
  | 'reloadPage'
  | 'openThreadList'
  | 'resolveAnalysis'
  | null;
// TELV1-296 #2b — extended with `openThreadList` so INVALID_INPUT
// can offer a "Start fresh thread" escape hatch as the secondary
// action while keeping `retry` (rephrase + retry) as the primary.
// Previously only `dismiss` was supported.
type SecondaryActionKey = 'dismiss' | 'openThreadList' | null;

interface CodeRow {
  title: string;
  description: string;
  severity: Severity;
  primaryAction: PrimaryActionKey;
  secondaryAction: SecondaryActionKey;
}

// ── CODE_MAP ───────────────────────────────────────────────────────────────
// One row per SafeErrorCode.  Keep in the same order as SAFE_ERROR_CODES in
// research-assistant.errors.ts so drift is easy to spot in code review.

const CODE_MAP: Record<SafeErrorCode, CodeRow> = {
  RATE_LIMIT_EXCEEDED: {
    title: 'Slow down',
    description:
      'You have reached the message rate limit. Please wait before sending another message.',
    severity: 'warning',
    primaryAction: 'retry',
    secondaryAction: null,
  },
  QUOTA_EXCEEDED: {
    title: 'Quota reached',
    description: 'This project has reached its analysis quota. Contact support to continue.',
    severity: 'error',
    primaryAction: 'contactSupport',
    secondaryAction: 'dismiss',
  },
  INVALID_INPUT: {
    title: "Couldn't process that",
    // TELV1-296 #2b — INVALID_INPUT after several productive turns
    // almost always means the conversation history landed in a state
    // Anthropic's request validator rejects (most often a
    // tool_use / tool_result id mismatch that the message cleaner
    // didn't catch). Retrying the same message just replays the same
    // bad state — QA reported being unable to continue. Send the
    // user to the thread list so they can start a fresh thread that
    // bypasses the corrupted history, and surface the correlation id
    // prominently so support has a paper trail.
    description:
      "The request was rejected. Try rephrasing — if that still fails, the conversation has hit a state we can't recover from automatically. Start a new thread to continue.",
    severity: 'warning',
    primaryAction: 'retry',
    secondaryAction: 'openThreadList',
  },
  PROJECT_NOT_FOUND: {
    title: 'Project unavailable',
    description:
      'The project could not be loaded. It may have been deleted or your access may have changed.',
    severity: 'error',
    primaryAction: 'reloadPage',
    secondaryAction: null,
  },
  ANALYSIS_OUTDATED: {
    title: 'Analysis is out of date',
    description:
      'The analysis must be re-run before you can continue. Open the dialog to resolve this.',
    severity: 'warning',
    // D.2-R3: label is "Resolve outdated analysis" — not "Re-run analysis"
    primaryAction: 'resolveAnalysis',
    secondaryAction: 'dismiss',
  },
  THREAD_NOT_FOUND: {
    title: 'Thread no longer exists',
    description:
      'This conversation thread has been deleted. Open the thread list to start a new one.',
    severity: 'warning',
    primaryAction: 'openThreadList',
    secondaryAction: null,
  },
  THREAD_FORBIDDEN: {
    title: 'No access to this thread',
    description:
      'You do not have permission to view this conversation. Open the thread list to continue.',
    severity: 'error',
    primaryAction: 'openThreadList',
    secondaryAction: null,
  },
  CONVERSATION_STATE_SAVE_FAILED: {
    title: "We couldn't save your conversation",
    description:
      'Failed to save conversation state. Your messages may not persist. Please try again.',
    severity: 'warning',
    primaryAction: 'retry',
    secondaryAction: 'dismiss',
  },
  CONCURRENT_TURN: {
    title: 'One message at a time',
    description:
      'A response is already being generated in this conversation. Please wait for it to finish before sending another message — this can happen when the same conversation is open in two tabs.',
    severity: 'info',
    primaryAction: null,
    secondaryAction: 'dismiss',
  },
  GENERIC: {
    title: 'Something went wrong',
    description: 'Something went wrong on our side. Our team has been notified — please try again.',
    severity: 'error',
    primaryAction: 'retry',
    secondaryAction: 'dismiss',
  },
};

// ── Severity styling ───────────────────────────────────────────────────────

const SEVERITY_STYLES: Record<
  Severity,
  { wrapper: string; icon: string; text: string; border: string }
> = {
  error: {
    wrapper: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-500',
    text: 'text-red-800',
  },
  warning: {
    wrapper: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'text-amber-600',
    text: 'text-amber-800',
  },
  info: {
    wrapper: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-500',
    text: 'text-blue-800',
  },
};

const SEVERITY_ICONS: Record<Severity, React.ComponentType<{ className?: string }>> = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

// ── Button styling helper ──────────────────────────────────────────────────

const ACTION_BTN_BASE =
  'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors focus:outline-none focus-visible:ring-2';

function actionBtnClass(severity: Severity) {
  const map: Record<Severity, string> = {
    error: 'text-red-700 border-red-300 hover:bg-red-100 focus-visible:ring-red-300',
    warning: 'text-amber-700 border-amber-300 hover:bg-amber-100 focus-visible:ring-amber-300',
    info: 'text-blue-700 border-blue-300 hover:bg-blue-100 focus-visible:ring-blue-300',
  };
  return cn(ACTION_BTN_BASE, map[severity]);
}

// ── Component ─────────────────────────────────────────────────────────────

/**
 * Presentational error banner for the Research Assistant SSE `error` event.
 *
 * Replaces the hard-coded single-template banner that previously collapsed all
 * 9 SafeErrorCodes into identical copy. See design spec D.2 + review D.2-R3.
 *
 * Positioning: render OUTSIDE the scroll container (as a sibling of
 * messagesContainerRef, not a descendant) so it stays visible on long threads.
 */
function ResearchAssistantErrorBanner({
  code,
  message: _message,
  correlationId,
  retriable,
  onRetry,
  onDismiss,
  onOpenThreadList,
  onReRunAnalysis,
  retryAfterSec,
}: ResearchAssistantErrorBannerProps) {
  const row = CODE_MAP[code] ?? CODE_MAP.GENERIC;
  const styles = SEVERITY_STYLES[row.severity];
  const SeverityIcon = SEVERITY_ICONS[row.severity];

  // Countdown timer for RATE_LIMIT_EXCEEDED
  const [countdown, setCountdown] = useState<number | null>(
    retryAfterSec !== undefined && retryAfterSec > 0 ? retryAfterSec : null,
  );

  // Correlation ID copy state
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (countdown === null || countdown <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => setCountdown((c) => (c !== null && c > 0 ? c - 1 : 0)), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(correlationId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked in non-HTTPS or restricted embeds — silent no-op
      // (same pattern as CopyButton.tsx:19-24).
    }
  }, [correlationId]);

  const isRetryDisabled = countdown !== null && countdown > 0;
  const shortId = correlationId.slice(0, 8);

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn(
        'shrink-0 px-5 py-3 border-b animate-in fade-in-50 slide-in-from-top-2 duration-200',
        styles.wrapper,
        styles.border,
      )}
      data-testid={`ra-error-banner-${code}`}
    >
      <div className="flex items-start gap-3 max-w-4xl mx-auto">
        <SeverityIcon className={cn('h-4 w-4 shrink-0 mt-0.5', styles.icon)} aria-hidden="true" />

        <div className="flex-1 min-w-0">
          {/* Title + description */}
          <p className={cn('text-sm font-medium', styles.text)}>{row.title}</p>
          <p className={cn('text-xs mt-0.5 break-words', styles.text, 'opacity-80')}>
            {row.description}
          </p>

          {/* Countdown chip for rate-limited state */}
          {code === 'RATE_LIMIT_EXCEEDED' && countdown !== null && countdown > 0 && (
            <p className={cn('text-xs mt-1 font-mono tabular-nums', styles.text, 'opacity-70')}>
              Retry in {countdown}s
            </p>
          )}

          {/* CorrelationId footer (all 9 codes) */}
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="text-xs text-neutral-500 font-mono" data-testid="correlation-id-label">
              ID: {shortId}
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-300"
              aria-label={copied ? 'Copied to clipboard' : 'Copy correlation ID to clipboard'}
              data-testid="correlation-id-copy-btn"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              <span aria-live="polite" className="sr-only">
                {copied ? 'Copied to clipboard' : ''}
              </span>
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          {/* Primary action */}
          {row.primaryAction === 'retry' && (onRetry || retriable) && (
            <button
              type="button"
              onClick={onRetry}
              disabled={isRetryDisabled}
              className={cn(
                actionBtnClass(row.severity),
                isRetryDisabled && 'opacity-50 cursor-not-allowed',
              )}
              data-testid="banner-action-retry"
            >
              <RotateCcw className="h-3 w-3" />
              {isRetryDisabled && countdown !== null ? `Wait ${countdown}s` : 'Try again'}
            </button>
          )}

          {row.primaryAction === 'contactSupport' && (
            <a
              href="mailto:support@tellet.ai"
              className={cn(actionBtnClass(row.severity))}
              data-testid="banner-action-contact-support"
            >
              <ExternalLink className="h-3 w-3" />
              Contact support
            </a>
          )}

          {row.primaryAction === 'reloadPage' && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={actionBtnClass(row.severity)}
              data-testid="banner-action-reload"
            >
              <RefreshCw className="h-3 w-3" />
              Reload page
            </button>
          )}

          {row.primaryAction === 'openThreadList' && onOpenThreadList && (
            <button
              type="button"
              onClick={onOpenThreadList}
              className={actionBtnClass(row.severity)}
              data-testid="banner-action-open-thread-list"
            >
              <List className="h-3 w-3" />
              Open thread list
            </button>
          )}

          {row.primaryAction === 'resolveAnalysis' && onReRunAnalysis && (
            <button
              type="button"
              onClick={onReRunAnalysis}
              className={actionBtnClass(row.severity)}
              data-testid="banner-action-resolve-analysis"
            >
              <RefreshCw className="h-3 w-3" />
              {/* D.2-R3: "Resolve outdated analysis" — NOT "Re-run analysis".
                  Clicking invalidates ['ra-analysis-schema-status', projectId]
                  so the OutdatedAnalysisDialog re-shows. It does NOT directly
                  kick off a pipeline run. */}
              Resolve outdated analysis
            </button>
          )}

          {/* Secondary action: openThreadList (TELV1-296 #2b — used by
              INVALID_INPUT as a "start fresh thread" escape hatch when
              a retry isn't going to help). */}
          {row.secondaryAction === 'openThreadList' && onOpenThreadList && (
            <button
              type="button"
              onClick={onOpenThreadList}
              className={cn(
                'text-xs font-medium rounded px-3 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 flex items-center gap-1.5',
                row.severity === 'error'
                  ? 'border border-red-200 text-red-700 hover:bg-red-50 focus-visible:ring-red-300'
                  : 'border border-amber-200 text-amber-700 hover:bg-amber-50 focus-visible:ring-amber-300',
              )}
              data-testid="banner-action-open-thread-list"
            >
              <List className="h-3.5 w-3.5" />
              Start fresh thread
            </button>
          )}

          {/* Secondary action: dismiss */}
          {row.secondaryAction === 'dismiss' && onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className={cn(
                'p-2 rounded transition-colors min-h-[28px] min-w-[28px] flex items-center justify-center focus:outline-none focus-visible:ring-2',
                {
                  error: 'text-red-400 hover:text-red-700 focus-visible:ring-red-300',
                  info: 'text-blue-500 hover:text-blue-700 focus-visible:ring-blue-300',
                  warning: 'text-amber-500 hover:text-amber-700 focus-visible:ring-amber-300',
                }[row.severity],
              )}
              aria-label="Dismiss error"
              data-testid="banner-action-dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResearchAssistantErrorBanner;
